// Session store. The React admin is cross-origin from the Go backend (Cloud
// Run), so it calls the API with a bearer access token that lives ONLY in
// memory. The refresh token is never readable from JavaScript: Go keeps it in
// the HttpOnly `ferry_refresh` cookie (host-only on the API host, SameSite=Lax;
// tools and api are same-site, so it travels with `credentials: 'include'`).
// A page load restores the session with `POST /auth/refresh`, which rotates the
// cookie and returns a fresh access token.

import { isPublicPathname } from '../features/session/publicRoutes';

/**
 * The pre-cookie build kept the refresh token here. It is read once to migrate
 * a still-signed-in browser, then removed. Nothing writes this key any more.
 */
const LEGACY_REFRESH_KEY = 'ferry.refreshToken';

let accessToken: string | null = null;

export function getAccessToken(): string | null {
  return accessToken;
}

export function setSession(tokens: { accessToken: string }): void {
  accessToken = tokens.accessToken;
}

/** True while this tab holds an access token. A reload starts without one. */
export function isAuthenticated(): boolean {
  return accessToken !== null;
}

const graphqlUri =
  (import.meta.env.VITE_GRAPHQL_URL as string | undefined) ?? '/graphql';

/**
 * Absolute API origin. The refresh cookie is host-only on the API host, so
 * `/auth/*`, the logout mutation and the WebAuthn ceremonies must hit it
 * directly, never through the Vite dev proxy (a proxied response would store
 * the cookie on the tools host). Local dev sets VITE_BACKEND_URL; hosted
 * builds derive it from the absolute VITE_GRAPHQL_URL. Empty means same origin.
 */
export function apiOrigin(): string {
  const backend = (import.meta.env.VITE_BACKEND_URL as string | undefined)?.trim();
  if (backend && /^https?:\/\//i.test(backend)) return backend.replace(/\/$/, '');
  try {
    return new URL(graphqlUri, window.location.origin).origin;
  } catch {
    return '';
  }
}

export function apiUrl(path: string): string {
  return `${apiOrigin()}${path}`;
}

/** Go answered the refresh with a GraphQL error that is not a plain rejection. */
export class RefreshAnswerError extends Error {}

interface GraphQLBody<T> {
  data?: T | null;
  errors?: { message?: string; extensions?: { code?: string } }[];
}

async function readJson<T>(res: Response, what: string): Promise<T> {
  try {
    return (await res.json()) as T;
  } catch {
    throw new Error(`${what}: response was not JSON`);
  }
}

/** In-flight refresh so React Strict Mode / parallel 401s cannot rotate twice. */
let refreshInFlight: Promise<string | null> | null = null;

/** True when Go rejected the refresh token itself, not a transport or internal error. */
function isRejectedRefresh(body: GraphQLBody<unknown>): boolean {
  const error = body.errors?.[0];
  if (!error) return false;
  if (error.extensions?.code === 'UNAUTHENTICATED') return true;
  return /invalid refresh token/i.test(error.message ?? '');
}

/**
 * Trades a refresh token for a session through the GraphQL `refreshSession`
 * mutation on the API origin. Go re-issues the HttpOnly cookie in the same
 * response; only the access token is kept here, the body's refresh token is
 * dropped. Used by local dev sign-in and the one-time legacy migration.
 * Returns the access token, null when the token is rejected, throws otherwise.
 */
export async function exchangeRefreshToken(refreshToken: string): Promise<string | null> {
  const res = await fetch(apiUrl('/graphql'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      query: `mutation($t: String!){ refreshSession(refreshToken: $t){ accessToken } }`,
      variables: { t: refreshToken },
    }),
  });
  if (!res.ok) throw new Error(`Refresh failed: ${res.status}`);
  const json = await readJson<GraphQLBody<{ refreshSession?: { accessToken?: string } | null }>>(
    res,
    'Refresh failed',
  );
  const token = json.data?.refreshSession?.accessToken;
  if (token) {
    setSession({ accessToken: token });
    return token;
  }
  if (isRejectedRefresh(json)) return null;
  throw new RefreshAnswerError(json.errors?.[0]?.message || 'Refresh failed');
}

function readLegacyRefreshToken(): string | null {
  try {
    return localStorage.getItem(LEGACY_REFRESH_KEY);
  } catch {
    return null;
  }
}

function dropLegacyRefreshToken(): void {
  try {
    localStorage.removeItem(LEGACY_REFRESH_KEY);
  } catch {
    // Storage blocked: nothing to remove.
  }
}

/**
 * Re-establishes the session from the refresh cookie. A browser that still has
 * the legacy localStorage token trades it once for the cookie and the key is
 * removed as soon as Go has answered, so it is never replayed. Returns the new
 * access token, null when there is no valid session (401/403), or throws on
 * transport / 5xx errors so a later call can retry.
 */
export async function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const legacy = readLegacyRefreshToken();
    if (legacy) {
      // Any answer from Go (success, rejection or a GraphQL error) drops the
      // key so it is never replayed; only a transport failure keeps it for a
      // retry. After an answer that is not a success, fall through to the cookie.
      try {
        const token = await exchangeRefreshToken(legacy);
        dropLegacyRefreshToken();
        if (token) return token;
      } catch (error) {
        if (!(error instanceof RefreshAnswerError)) throw error;
        dropLegacyRefreshToken();
      }
    }

    const res = await fetch(apiUrl('/auth/refresh'), {
      method: 'POST',
      credentials: 'include',
    });
    // 401/403: no valid session. Anything else that is not ok (5xx, network)
    // throws so the caller can retry instead of treating it as signed out.
    if (res.status === 401 || res.status === 403) {
      accessToken = null;
      return null;
    }
    if (!res.ok) throw new Error(`Refresh failed: ${res.status}`);
    const json = await readJson<{ accessToken?: string }>(res, 'Refresh failed');
    if (!json.accessToken) throw new Error('Refresh failed');
    setSession({ accessToken: json.accessToken });
    return json.accessToken;
  })().finally(() => {
    refreshInFlight = null;
  });

  return refreshInFlight;
}

/**
 * Ends the session on the server: the GraphQL `logout` mutation revokes the
 * refresh token behind the HttpOnly cookie (nothing is passed from JS) and
 * Go expires the cookie. `everywhere` also ends the user's other sessions.
 * BAD_USER_INPUT (no cookie, or already revoked) means already signed out.
 * Never retried: replaying a rotated token revokes the whole family. On
 * success the in-memory token is cleared; on failure it throws and keeps the
 * session so the caller can tell the user.
 */
export async function signOut(options: { everywhere?: boolean } = {}): Promise<void> {
  const res = await fetch(apiUrl('/graphql'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      query: `mutation($all: Boolean){ logout(everywhere: $all) }`,
      variables: { all: options.everywhere === true },
    }),
  });
  // A 4xx with a GraphQL body still parses; only a dead server is a failure.
  if (!res.ok && res.status >= 500) throw new Error(`Sign out failed: ${res.status}`);
  const json = await readJson<GraphQLBody<{ logout?: boolean }>>(res, 'Sign out failed');
  const error = json.errors?.[0];
  if (error && error.extensions?.code !== 'BAD_USER_INPUT') {
    throw new Error(error.message || 'Sign out failed');
  }
  accessToken = null;
}

let logoutInFlight: Promise<void> | null = null;

/**
 * Forced sign-out for idle timeout and a rejected session: tries `signOut`
 * once, clears the in-memory token whatever the answer was, then navigates to
 * login. No-ops the navigation on public routes (avoids bouncing off /login
 * itself).
 */
export function logout(redirectPath?: string): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (logoutInFlight) return logoutInFlight;
  logoutInFlight = (async () => {
    try {
      await signOut();
    } catch {
      // Best effort: the user is leaving either way.
    }
    accessToken = null;
    if (isPublicPathname(window.location.pathname)) return;
    const path = redirectPath ?? window.location.pathname + window.location.search;
    window.location.assign(`/login?redirect=${encodeURIComponent(path)}`);
  })().finally(() => {
    logoutInFlight = null;
  });
  return logoutInFlight;
}
